import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from '@tanstack/react-query';
import { config } from './config';

interface HttpClientConfig extends AxiosRequestConfig {
  baseURL: string;
  responseType: 'json' | 'blob';
  timeout: number;
  headers: {
    'X-Requested-With': string;
    'Content-Type': string;
    Authorization: string;
  };
}

export const goClient = axios.create({
  baseURL: config.GO_API_BASE_URL,
  responseType: 'json',
  timeout: 20000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    // Authorization removed - set dynamically via interceptor
  },
} as Omit<HttpClientConfig, 'headers'> & {
  headers: Omit<HttpClientConfig['headers'], 'Authorization'>;
});

// Add request interceptor to dynamically set Authorization header
// Only set if not already set (to allow custom headers)
goClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const formGoClient = axios.create({
  baseURL: config.GO_API_BASE_URL,
  responseType: 'json',
  timeout: 10000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'multipart/form-data',
    // Authorization removed - set dynamically via interceptor
  },
} as Omit<HttpClientConfig, 'headers'> & {
  headers: Omit<HttpClientConfig['headers'], 'Authorization'>;
});

// Add request interceptor to dynamically set Authorization header
// Only set if not already set (to allow custom headers)
formGoClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const goRequest = async <T = any>(
  options: AxiosRequestConfig
): Promise<T> => {
  const onSuccess = (response: AxiosResponse<T>): T => {
    console.debug('Request Successful!', response);
    return response.data;
  };

  const onError = (error: AxiosError): never => {
    console.error('Request Failed:', error.toJSON());
    if (error.response?.status === 401) {
      // Save current URL to localStorage before redirecting (fallback for 401 errors)
      // Router.jsx uses query params, but we can't add them with window.location.href
      const currentPath = window.location.pathname + window.location.search;
      const email = localStorage.getItem('email');
      localStorage.clear();
      if (email) {
        localStorage.setItem('email', email);
      }
      // Store returnUrl in localStorage as fallback (Login.tsx checks query params first)
      localStorage.setItem('returnUrl', currentPath);
      window.location.href = '/login';
    }
    throw error.response?.data || error.message;
  };
  return goClient({ ...options })
    .then(onSuccess)
    .catch(onError);
};

// Generic type for API functions
type ApiFunction<TParams = any, TResponse = any> = (
  params?: TParams
) => Promise<TResponse>;

// Generic type for success callback
type SuccessCallback<TData = any> = (data: TData) => void;

// Generic type for error callback
type ErrorCallback<TError = any> = (error: TError) => void;

export const useApiGet = <TData = any, TParams = any>(
  key: QueryKey,
  fn: ApiFunction<TParams, TData>,
  options?: Omit<
    UseQueryOptions<TData, Error, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
  stringParams?: TParams
) => {
  return useQuery<TData, Error, TData, QueryKey>({
    queryKey: key,
    queryFn: stringParams ? () => fn(stringParams) : () => fn(),
    ...options,
  });
};

export const useApiSend = <TData = any, TParams = any>(
  fn: ApiFunction<TParams, TData>,
  success?: SuccessCallback<TData>,
  error?: ErrorCallback<Error>,
  invalidateKey?: QueryKey[],
  options?: Omit<
    UseMutationOptions<TData, Error, TParams, unknown>,
    'mutationFn' | 'onSuccess' | 'onError'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TParams>({
    mutationFn: fn,
    onSuccess: (data: TData) => {
      if (invalidateKey) {
        invalidateKey.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }
      if (success) {
        success(data);
      }
    },
    onError: error,
    retry: 0,
    ...options,
  });
};

// Additional utility types for common use cases
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
  error?: string;
}

export interface PaginatedResponse<T = any> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
}

const FALLBACK_ERROR_MESSAGE =
  'Error submitting request. If the problem persists, please contact support.';

/**
 * Extracts a user-facing message from an API error. Prefers backend `details`, then `error`, then `message`.
 * Use when displaying errors from useApiSend / goRequest so the UI shows the actual backend message.
 */
export function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const o = error as Record<string, unknown>;
    if (typeof o.details === 'string' && o.details.trim()) return o.details;
    if (typeof o.error === 'string' && o.error.trim()) return o.error;
    if (typeof o.message === 'string' && o.message.trim()) return o.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return FALLBACK_ERROR_MESSAGE;
}
